// JSON shapes returned by the batch-4 RPCs (supabase/schemas/24_gavion_*.sql) and by the local client.
export type FunnelMode = "cohort" | "snapshot";
export type ReportFilters = {
  sales_id?: number | null;
  trade?: string | null;
  region?: string | null;
  source?: string | null;
  category?: string | null;
  mode?: FunnelMode;
  scope?: "me" | "team";
};
export type ProspectingKey =
  | "called"
  | "reached"
  | "interested"
  | "demos_booked"
  | "demos_held";
export type FunnelStageRow = {
  stage: string;
  count: number;
  amount: number;
  /** GAVION deal amount visibility (72_): the viewer's hidden amounts this sum leaves out. */
  amount_hidden?: number;
  reached: number;
  conv_next: number | null;
  median_days: number | null;
};
export type FunnelReport = {
  prospecting: { key: ProspectingKey; count: number }[];
  deals: FunnelStageRow[];
  win_rate: number | null;
  avg_won: number | null;
  cycle_median_days: number | null;
  /** GAVION (72_): how many of the viewer's deals have a hidden amount (« Certaines valeurs sont masquées »). */
  amounts_hidden?: number;
};
export type RepActivityRow = {
  sales_id: number;
  name: string;
  dials: number;
  contacts: number;
  voicemails: number;
  demos: number;
  talk_seconds: number;
  first_call_at: string | null;
  last_call_at: string | null;
  sessions: number;
  dials_per_hour: number | null;
};
export type RepDayRow = Omit<RepActivityRow, "dials_per_hour"> & {
  day: string;
};
export type DayRow = { day: string; dials: number; contacts: number };
export type ActivityReport = {
  scope: "me" | "team";
  totals: {
    dials: number;
    contacts: number;
    voicemails: number;
    demos: number;
    talk_seconds: number;
    contact_rate: number | null;
  };
  by_rep: RepActivityRow[];
  by_rep_day: RepDayRow[];
  by_day: DayRow[];
  pipeline: {
    open_amount: number;
    weighted_amount: number;
    won_this_month: number;
  };
};
export type CallOutcomesReport = {
  by_outcome: { outcome: string; count: number }[];
  by_trade: { trade: string | null; dials: number; contacts: number }[];
  by_region: { region: string | null; dials: number; contacts: number }[];
  heatmap: { dow: number; hour: number; dials: number; contacts: number }[];
};
export type LossReasonRow = {
  reason: string;
  sales_id: number | null;
  rep_name: string | null;
  count: number;
  amount: number;
  amount_hidden?: number; // GAVION (72_)
};
export type DealSourceRow = {
  source: string;
  deals: number;
  won: number;
  won_amount: number;
  won_amount_hidden?: number; // GAVION (72_)
};
export type DataQualityReport = {
  total: number;
  invalid_phone: number;
  no_email: number;
  unassigned: number;
  duplicate_phone: number;
  duplicate_name_city: number;
  stale_90d: number;
};
export type ExportTable =
  | "funnel_deals"
  | "funnel_prospecting"
  | "activity_by_rep_day"
  | "call_outcomes"
  | "call_heatmap"
  | "loss_reasons"
  | "deal_sources"
  | "data_quality";

/** X1 "Classement": today, ISO week to date, month to date (local days in the configured zone). */
export type LeaderboardPeriod = "today" | "week" | "month";
export type LeaderboardRow = {
  sales_id: number;
  name: string;
  dials: number;
  conversations: number;
  demos: number;
  deals_won: number;
  /** Incoming calls answered (82_gavion_inbound_ranking.sql); absent from an older server. */
  inbound?: number;
};
export type LeaderboardReport = {
  period: LeaderboardPeriod;
  from: string;
  to: string;
  timezone: string;
  rows: LeaderboardRow[];
};

/** X5 « Meilleures heures d'appel »: last 30, 90 or 365 local days. */
export type CallHoursDays = 30 | 90 | 365;
/** Reps: "me" (default) or "team" (everyone, aggregated). Admins use sales_id (null = everyone). */
export type CallHoursFilters = {
  scope?: "me" | "team";
  sales_id?: number | null;
  trade?: string | null;
  region?: string | null;
};
/** One ISO weekday (1 = Monday) × hour (0–23) in the lead's local time. */
export type CallHoursCell = {
  dow: number;
  hour: number;
  dials: number;
  conversations: number;
  demos: number;
};
export type CallHoursReport = {
  days: CallHoursDays;
  from: string;
  to: string;
  timezone: string;
  /** A heatmap cell with fewer dials is greyed (« pas assez de données »). */
  min_dials: number;
  /** A recommended window needs at least this many dials. */
  min_sample: number;
  cells: CallHoursCell[];
  /** Up to 3 windows: connect rate, then demo rate, then dials. */
  recommendations: CallHoursCell[];
};

/** Rep detail (supabase/schemas/48_gavion_rep_stats.sql): counts, possibly team averages, and their rates. */
export type RepKpis = {
  dials: number;
  conversations: number;
  demos: number;
  deals_won: number;
  revenue_won: number;
  session_seconds: number;
  demos_closed: number;
  callbacks_due: number;
  callbacks_kept: number;
  first_conversation_leads: number;
  first_conversation_attempts: number;
  conversation_rate: number | null;
  demo_rate: number | null;
  close_rate: number | null;
  dials_per_session_hour: number | null;
  attempts_per_first_conversation: number | null;
  callbacks_kept_rate: number | null;
};
export type RepCounts = Omit<
  RepKpis,
  | "conversation_rate"
  | "demo_rate"
  | "close_rate"
  | "dials_per_session_hour"
  | "attempts_per_first_conversation"
  | "callbacks_kept_rate"
>;
export type RepSegmentRow = {
  key: string | null;
  dials: number;
  conversations: number;
  demos: number;
};
export type RepTrendDay = {
  day: string;
  dials: number;
  conversations: number;
  demos: number;
};
export type RepStatsReport = {
  sales_id: number;
  name: string | null;
  from: string;
  to: string;
  previous_from: string;
  previous_to: string;
  timezone: string;
  callback_window_minutes: number;
  kpis: RepKpis;
  previous: RepKpis;
  /** Averages per leaderboard member for the same period; null when the team is empty. */
  team: RepKpis | null;
  team_size: number;
  /** GAVION (72_): the team average leaves out sales whose amount is hidden from the viewer. */
  amounts_hidden?: boolean;
  by_day: RepTrendDay[];
  by_outcome: { outcome: string; count: number }[];
  not_interested: { reason: string; count: number }[];
  by_trade: RepSegmentRow[];
  by_region: RepSegmentRow[];
};
export type ConversationOutcome =
  | "talked_callback"
  | "demo_booked"
  | "brochure_sent"
  | "not_interested";
export type RepConversation = {
  id: number;
  dialed_at: string;
  outcome: ConversationOutcome;
  note: string | null;
  company_id: number | null;
  company_name: string | null;
  contact_id: number | null;
  contact_name: string | null;
  callback_at: string | null;
  demo_at: string | null;
  not_interested_reason: string | null;
  deal_id: number | null;
  deal_name: string | null;
  deal_stage: string | null;
  recording_path: string | null;
  billsec_s: number | null;
  meeting_note: {
    id: number;
    title: string | null;
    share_url: string | null;
    started_at: string | null;
  } | null;
};
export type RepConversationsPage = { total: number; rows: RepConversation[] };
export type RepConversationsQuery = {
  outcome?: ConversationOutcome | null;
  limit: number;
  offset: number;
};

/** What a deal amount means, for the payback (cost_settings.amount_basis). */
export type AmountBasis = "one_time" | "monthly" | "annual";
export type CostRatios = {
  cost_per_dial: number | null;
  cost_per_conversation: number | null;
  cost_per_demo: number | null;
  cac: number | null;
  revenue_cost_ratio: number | null;
  payback_months: number | null;
};
type CostCounts = {
  dials: number;
  conversations: number;
  demos: number;
  deals_won: number;
  revenue_won: number;
  session_seconds: number;
};
export type RepCostRow = CostCounts &
  CostRatios & {
    sales_id: number;
    name: string;
    has_compensation: boolean;
    pay: number;
    commission: number;
    fixed_share: number;
    total: number;
  };
export type TeamCostRow = CostCounts &
  CostRatios & {
    pay: number;
    commission: number;
    fixed: number;
    marketing: number;
    total: number;
    /** Fixed costs of a period without any session time (not allocated to a rep). */
    unallocated_fixed: number;
  };
export type ChannelCostRow = {
  channel: string;
  spend: number;
  deals_won: number;
  revenue_won: number;
  /** Channel marketing spend ÷ deals won from that channel (not the CAC: no pay, commission or fixed cost). */
  marketing_cost_per_deal: number | null;
};
export type AcquisitionCostsReport = {
  from: string;
  to: string;
  timezone: string;
  /** False until an admin enters a compensation or a fixed cost: the UI shows an empty state, never numbers. */
  has_costs: boolean;
  amount_basis: AmountBasis | null;
  team: TeamCostRow;
  reps: RepCostRow[];
  channels: ChannelCostRow[];
};

/** GAVION team sessions (supabase/schemas/59_gavion_team_sessions.sql): one dial session, counts from its calls. */
export type SessionSummary = {
  id: number;
  sales_id: number;
  name: string;
  mode: string;
  paused: boolean;
  started_at: string;
  ended_at: string | null;
  /** Effective end (private.dial_session_end): ended_at, last activity, or now while live. */
  ends_at: string;
  live: boolean;
  duration_seconds: number;
  dials: number;
  conversations: number;
  demos: number;
  talk_seconds: number;
  /** Null under one minute of session. */
  dials_per_hour: number | null;
};
export type TeamSessionsTotals = {
  sessions: number;
  duration_seconds: number;
  dials: number;
  conversations: number;
  demos: number;
  dials_per_hour: number | null;
};
export type TeamSessionsReport = {
  from: string;
  to: string;
  timezone: string;
  sales_id: number | null;
  total: number;
  totals: TeamSessionsTotals;
  /** One page, newest first. */
  rows: SessionSummary[];
};
export type TeamSessionsQuery = {
  salesId: number | null;
  limit: number;
  offset: number;
};
export type LiveSession = SessionSummary & {
  last_activity_at: string;
  /** The rep's open call (the lead on screen), if any. */
  current_call: {
    call_id: number;
    since: string;
    company_id: number | null;
    company_name: string | null;
    contact_id: number | null;
    contact_name: string | null;
  } | null;
};
export type SessionCall = {
  id: number;
  dialed_at: string;
  claimed_at: string | null;
  direction: string;
  /** Null while the call is still open. */
  outcome: string | null;
  counts_as_dial: boolean;
  company_id: number | null;
  company_name: string | null;
  contact_id: number | null;
  contact_name: string | null;
  billsec_s: number | null;
  note: string | null;
  meeting_note: {
    id: number;
    title: string | null;
    share_url: string | null;
    started_at: string | null;
  } | null;
};
export type SessionDetail = { session: SessionSummary; calls: SessionCall[] };

export interface ReportsClient {
  /** public.gavion_team_sessions (59_gavion_team_sessions.sql): admins only. */
  teamSessions(
    from: string,
    to: string,
    query: TeamSessionsQuery,
  ): Promise<TeamSessionsReport>;
  /** public.gavion_live_sessions: admins only. */
  liveSessions(): Promise<LiveSession[]>;
  /** public.gavion_session_calls: admins only; not_found for an unknown session. */
  sessionCalls(sessionId: number): Promise<SessionDetail>;
  /** public.gavion_rep_stats (48_gavion_rep_stats.sql): admin, or the rep themself. */
  repStats(salesId: number, from: string, to: string): Promise<RepStatsReport>;
  /** public.gavion_rep_conversations: reached-human calls of the rep, one page. */
  repConversations(
    salesId: number,
    from: string,
    to: string,
    query: RepConversationsQuery,
  ): Promise<RepConversationsPage>;
  /** public.gavion_acquisition_costs: admins only. */
  acquisitionCosts(from: string, to: string): Promise<AcquisitionCostsReport>;
  /** public.gavion_call_hours (supabase/schemas/35_gavion_call_hours.sql): aggregates only, readable by reps. */
  callHours(
    days: CallHoursDays,
    filters: CallHoursFilters,
  ): Promise<CallHoursReport>;
  /** public.gavion_leaderboard (supabase/schemas/30_gavion_goals.sql): counts and names only, readable by reps. */
  leaderboard(period: LeaderboardPeriod): Promise<LeaderboardReport>;
  funnel(
    from: string,
    to: string,
    filters: ReportFilters,
  ): Promise<FunnelReport>;
  activity(
    from: string,
    to: string,
    filters: ReportFilters,
  ): Promise<ActivityReport>;
  callOutcomes(
    from: string,
    to: string,
    filters: ReportFilters,
  ): Promise<CallOutcomesReport>;
  lossReasons(
    from: string,
    to: string,
    filters: ReportFilters,
  ): Promise<LossReasonRow[]>;
  dealSources(
    from: string,
    to: string,
    filters: ReportFilters,
  ): Promise<DealSourceRow[]>;
  dataQuality(): Promise<DataQualityReport>;
  exportRows(
    table: ExportTable,
    from: string,
    to: string,
    filters: ReportFilters,
  ): Promise<Record<string, unknown>[]>;
}

export type ReportErrorCode =
  | "forbidden"
  | "not_found"
  | "invalid_period"
  | "invalid_filters"
  | "unknown";
export class ReportError extends Error {
  readonly code: ReportErrorCode;
  constructor(code: ReportErrorCode, message?: string) {
    super(message ?? code);
    this.name = "ReportError";
    this.code = code;
  }
}
